import {
  SubmissionStatus,
  UnitType,
  WasteContainerStatus,
  WasteEpisodicEventStatus,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { formatQuantity } from "../Containers/containerDisplay";

export const NO_VALUE_PLACEHOLDER = "—";

export const EVENT_TYPE_LABELS: Record<WasteEpisodicEventType, string> = {
  [WasteEpisodicEventType.Planned]: "Planned",
  [WasteEpisodicEventType.Unplanned]: "Unplanned",
};

export const EVENT_STATUS_LABELS: Record<WasteEpisodicEventStatus, string> = {
  [WasteEpisodicEventStatus.Active]: "Active",
  [WasteEpisodicEventStatus.Closed]: "Closed",
  [WasteEpisodicEventStatus.Expired]: "Expired",
};

/**
 * A compact display range for an event's start/end `Date` scalars, e.g.
 * "Jun 28 – Jul 3, 2026". The year is dropped from the start side when both
 * dates fall in the same calendar year, matching the container date-handling
 * idiom of parsing in UTC (see `formatContainerDate`) since these are
 * date-only values with no time-of-day to shift.
 */
export function formatEventWindow(startDate: string, endDate: string): string {
  const start = DateTime.fromISO(startDate, { zone: "utc" });
  const end = DateTime.fromISO(endDate, { zone: "utc" });
  if (!start.isValid || !end.isValid) {
    return NO_VALUE_PLACEHOLDER;
  }
  const startFormat = start.year === end.year ? "MMM d" : "MMM d, yyyy";
  return `${start.toFormat(startFormat)} – ${end.toFormat("MMM d, yyyy")}`;
}

/** Minimal shape `resolveNotificationCellState` needs from a submission row. */
export type NotificationCellSubmission = { status: SubmissionStatus };

/**
 * The event's completed EPA filing, if any. Shared by the ledger's
 * Notification cell and the active-event card's notification banner so both
 * apply the same "a completed filing wins" rule.
 */
export function filedSubmission<S extends NotificationCellSubmission>(event: {
  submissions: S[];
}): S | undefined {
  return event.submissions.find(
    (submission) => submission.status === SubmissionStatus.FilingComplete
  );
}

/**
 * The event's in-progress submission, if nothing has been filed yet. Shared
 * by the ledger's Notification cell and the active-event card's notification
 * banner.
 */
export function openSubmission<S extends NotificationCellSubmission>(event: {
  submissions: S[];
}): S | undefined {
  return event.submissions.find(
    (submission) => submission.status !== SubmissionStatus.FilingComplete
  );
}

export type NotificationCellState =
  | { kind: "filed" }
  | { kind: "submissionInProgress" }
  | { kind: "notifiedEpa"; notifiedEpaAt: string; epaFormReference: string | null }
  | { kind: "notFiled" };

/**
 * Priority for the ledger's Notification cell: a completed filing always wins
 * over one still in progress; a manually recorded EPA notification is the
 * fallback once there's no submission at all; otherwise nothing has been
 * filed. Pulled out of the cell renderer so the priority logic is testable
 * without rendering.
 */
export function resolveNotificationCellState(event: {
  submissions: NotificationCellSubmission[];
  notifiedEpaAt?: string | null;
  epaFormReference?: string | null;
}): NotificationCellState {
  if (filedSubmission(event)) {
    return { kind: "filed" };
  }
  if (openSubmission(event)) {
    return { kind: "submissionInProgress" };
  }
  if (event.notifiedEpaAt) {
    return {
      kind: "notifiedEpa",
      notifiedEpaAt: event.notifiedEpaAt,
      epaFormReference: event.epaFormReference ?? null,
    };
  }
  return { kind: "notFiled" };
}

/** Length of an episodic event's accumulation window, in days. */
export const EVENT_WINDOW_DAYS = 60;

/**
 * Whole days elapsed since `startDate` opened the event's accumulation
 * window, clamped to `[0, EVENT_WINDOW_DAYS]`. `now` is injectable so callers
 * (and tests) don't depend on the wall clock.
 */
export function daysUsedInWindow(startDate: string, now: DateTime = DateTime.now()): number {
  return Math.min(
    EVENT_WINDOW_DAYS,
    Math.max(0, Math.floor(now.diff(DateTime.fromISO(startDate), "days").days))
  );
}

/**
 * True once `accumulationDeadline` (a date-only scalar, parsed in UTC like
 * `formatEventWindow`) falls before today — the window has ended even though
 * the event's status hasn't been flipped to CLOSED/EXPIRED yet. `now` is
 * injectable for tests.
 */
export function isEventWindowLapsed(
  accumulationDeadline: string,
  now: DateTime = DateTime.now()
): boolean {
  return (
    DateTime.fromISO(accumulationDeadline, { zone: "utc" }).startOf("day") <
    now.toUTC().startOf("day")
  );
}

/**
 * Route for a waste submission linked from an episodic event card. The
 * guided stepper route additionally gates on the `WasteSubmissionStepper`
 * feature flag (see `WasteSubmissionStepper`'s redirect guard) — a tenant
 * with container tracking but not that flag bounces to the read-only detail
 * view, so callers pass that flag's value rather than always linking to the
 * stepper.
 */
export function episodicSubmissionRoute(
  tenantId: string,
  submissionId: string,
  stepperEnabled: boolean
): string {
  const base = `/o/${tenantId}/waste/submissions/${submissionId}`;
  return stepperEnabled ? `${base}/stepper` : base;
}

type UnshippedWasteContainer = {
  amount?: number | null;
  amountUnit?: UnitType | null;
  status: WasteContainerStatus;
};

export type UnshippedEventWaste = {
  containerCount: number;
  /** Summed amount with unit, only when every counted container shares one unit. */
  quantityLabel: string | null;
};

/**
 * The event waste still on site: containers not yet shipped whose amount is
 * positive or unknown. A null amount deliberately counts as waste-on-hand —
 * an unknown quantity is not evidence the container is empty.
 */
export function unshippedEventWaste(containers: UnshippedWasteContainer[]): UnshippedEventWaste {
  const holding = containers.filter(
    (container) =>
      container.status !== WasteContainerStatus.Shipped &&
      (container.amount == null || container.amount > 0)
  );

  const units = new Set(holding.map((container) => container.amountUnit));
  const allAmountsKnown = holding.every((container) => container.amount != null);
  let quantityLabel: string | null = null;
  if (holding.length > 0 && allAmountsKnown && units.size === 1) {
    const [unit] = units;
    const total = holding.reduce((sum, container) => sum + (container.amount ?? 0), 0);
    quantityLabel = formatQuantity(total, unit);
  }

  return { containerCount: holding.length, quantityLabel };
}
